"use client";

import {
  Button,
  Content,
  Header,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderName,
  HeaderNavigation,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavMenu,
  SkipToContent,
  Theme,
} from "@carbon/react";
import {
  Book,
  Chat,
  ChatBot,
  Document,
  Forum,
  Home,
  Search,
} from "@carbon/react/icons";
import {
  type ComponentType,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { saveSideNavExpandedAction } from "@/app/(app)/shell-actions";
import { platformLogoutAction } from "@/app/platform/actions";
import { AppSideNavLink } from "@/components/AppSideNavLink";
import { ChatNavList } from "@/components/ChatNavList";
import { PresenceSync } from "@/components/PresenceSync";
import { TimeZoneSync } from "@/components/TimeZoneSync";
import { UserMenu } from "@/components/UserMenu";
import { adminHomeHref } from "@/lib/ui/admin";
import { isPublicLandingPath } from "@/lib/ui/landing";
import { loginCopy } from "@/lib/ui/login-copy";
import {
  isPlatformNavCurrent,
  isPlatformPath,
  platformCopy,
  platformHref,
  platformNavItems,
} from "@/lib/ui/platform";
import {
  carbonThemeFor,
  type AppearanceMode,
} from "@/lib/ui/profile";
import {
  isChatNavActive,
  newChatHref,
  type ChatFolderHistoryItem,
  type ChatHistoryItem,
} from "@/lib/ui/chat-history";
import {
  appNavItems,
  canShowAdminMenu,
  isNavActive,
  SIDE_NAV_OVERLAY_MEDIA,
  sideNavExpandedInViewport,
  type AppNavId,
} from "@/lib/ui/nav";
import { shouldToggleSideNavOnBackgroundClick } from "@/lib/ui/side-nav-shell";

type AppShellProps = {
  children: ReactNode;
  userName?: string;
  userEmail?: string;
  organizationName?: string;
  canManageAi?: boolean;
  canManageUsers?: boolean;
  canAuditMessenger?: boolean;
  canInspectAccess?: boolean;
  appearance?: AppearanceMode;
  sideNavExpanded?: boolean;
  conversations?: ChatHistoryItem[];
  chatFolders?: ChatFolderHistoryItem[];
  timeZoneCookie?: string;
  platformUserName?: string;
  platformUserEmail?: string;
};

function sideNavIcon(Icon: ComponentType<{ size?: number }>) {
  function SideNavGlyph(props: { size?: number }) {
    return <Icon size={20} {...props} />;
  }
  return SideNavGlyph;
}

const NAV_ICONS: Record<AppNavId, ComponentType<{ size?: number }>> = {
  home: sideNavIcon(Home),
  search: sideNavIcon(Search),
  chat: sideNavIcon(Chat),
  documents: sideNavIcon(Document),
  wiki: sideNavIcon(Book),
  agents: sideNavIcon(ChatBot),
  transcripts: sideNavIcon(Forum),
};

function useShellSideNav(savedExpanded: boolean) {
  const [overlay, setOverlay] = useState(false);
  const [expanded, setExpanded] = useState(savedExpanded);

  useLayoutEffect(() => {
    const media = window.matchMedia(SIDE_NAV_OVERLAY_MEDIA);
    const sync = () => {
      const nextOverlay = media.matches;
      setOverlay(nextOverlay);
      setExpanded(sideNavExpandedInViewport(savedExpanded, nextOverlay));
    };
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [savedExpanded]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExpanded(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (!overlay) {
      void saveSideNavExpandedAction(next);
    }
  }

  return { expanded, toggle, overlay };
}

export function AppShell({
  children,
  userName,
  userEmail,
  organizationName,
  canManageAi = false,
  canManageUsers = false,
  canAuditMessenger = false,
  canInspectAccess = false,
  appearance = "LIGHT",
  sideNavExpanded = true,
  conversations = [],
  chatFolders = [],
  timeZoneCookie = "",
  platformUserName,
  platformUserEmail,
}: AppShellProps) {
  const pathname = usePathname();
  const onPlatform = isPlatformPath(pathname);
  const showLanding = !userName && !onPlatform && isPublicLandingPath(pathname);
  const sideNav = useShellSideNav(sideNavExpanded);

  return (
    <AppTheme mode={appearance}>
      {userName ? <PresenceSync /> : null}
      {onPlatform && platformUserName ? (
        <>
          <Header aria-label="PinWiki" className="platform-header">
            <SkipToContent />
            <HeaderMenuButton
              aria-label={
                sideNav.expanded ? "Свернуть меню" : "Открыть меню"
              }
              isActive={sideNav.expanded}
              onClick={sideNav.toggle}
            />
            <HeaderName href={platformHref()} prefix="PinWiki" as={NextLink}>
              {platformCopy.brand}
            </HeaderName>
            <HeaderNavigation aria-label={platformCopy.brand}>
              {platformNavItems().map((item) => (
                <HeaderMenuItem
                  key={item.href}
                  as={NextLink}
                  href={item.href}
                  isCurrentPage={isPlatformNavCurrent(pathname, item.href)}
                >
                  {item.label}
                </HeaderMenuItem>
              ))}
            </HeaderNavigation>
            <HeaderGlobalBar>
              <span className="platform-header-user">
                <span className="platform-header-name">{platformUserName}</span>
                {platformUserEmail ? (
                  <span className="platform-header-email">{platformUserEmail}</span>
                ) : null}
              </span>
              <form action={platformLogoutAction}>
                <Button type="submit" kind="ghost" size="md">
                  {platformCopy.logout}
                </Button>
              </form>
            </HeaderGlobalBar>
            <SideNav
              className="app-side-nav platform-side-nav"
              aria-label={platformCopy.brand}
              expanded={sideNav.overlay && sideNav.expanded}
              isPersistent={false}
              addMouseListeners={false}
              addFocusListeners={false}
              onOverlayClick={() => {
                if (sideNav.expanded) {
                  sideNav.toggle();
                }
              }}
            >
              <SideNavItems>
                {platformNavItems().map((item) => (
                  <SideNavLink
                    key={item.href}
                    href={item.href}
                    as={NextLink}
                    isActive={isPlatformNavCurrent(pathname, item.href)}
                    onClick={() => {
                      if (sideNav.overlay && sideNav.expanded) {
                        sideNav.toggle();
                      }
                    }}
                  >
                    {item.label}
                  </SideNavLink>
                ))}
              </SideNavItems>
            </SideNav>
          </Header>
          <Content id="main-content" className="platform-shell">
            {children}
          </Content>
        </>
      ) : onPlatform ? (
        <>
          <Header aria-label="PinWiki">
            <HeaderName href="/platform/login" prefix="PinWiki" as={NextLink}>
              {platformCopy.brand}
            </HeaderName>
          </Header>
          <Content className="login-shell">{children}</Content>
        </>
      ) : userName ? (
        <>
          <TimeZoneSync saved={timeZoneCookie} />
          <Header aria-label="PinWiki">
            <SkipToContent />
            <HeaderMenuButton
              aria-label={
                sideNav.expanded ? "Свернуть меню" : "Открыть меню"
              }
              aria-expanded={sideNav.expanded}
              isActive={sideNav.expanded}
              isCollapsible
              onClick={sideNav.toggle}
            />
            <HeaderName href="/" prefix="PinWiki" as={NextLink}>
              {organizationName ?? loginCopy.brand}
            </HeaderName>
          </Header>
          <AppSideNav
            expanded={sideNav.expanded}
            overlay={sideNav.overlay}
            onToggle={sideNav.toggle}
            onOverlayClick={sideNav.toggle}
            canManageAi={canManageAi}
            canManageUsers={canManageUsers}
            canAuditMessenger={canAuditMessenger}
            canInspectAccess={canInspectAccess}
            conversations={conversations}
            chatFolders={chatFolders}
            userName={userName}
            userEmail={userEmail}
          />
          <Content id="main-content">{children}</Content>
        </>
      ) : showLanding ? (
        children
      ) : (
        <>
          <Header aria-label="PinWiki">
            <HeaderName href="/" prefix="PinWiki" as={NextLink}>
              {loginCopy.brand}
            </HeaderName>
          </Header>
          <Content className="login-shell">{children}</Content>
        </>
      )}
    </AppTheme>
  );
}

function AppTheme({
  mode,
  children,
}: {
  mode: AppearanceMode;
  children: ReactNode;
}) {
  const [prefersDark, setPrefersDark] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => setPrefersDark(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return <Theme theme={carbonThemeFor(mode, prefersDark)}>{children}</Theme>;
}

function AppSideNav({
  expanded,
  overlay,
  onToggle,
  onOverlayClick,
  canManageAi,
  canManageUsers,
  canAuditMessenger,
  canInspectAccess,
  conversations,
  chatFolders,
  userName,
  userEmail,
}: {
  expanded: boolean;
  overlay: boolean;
  onToggle: () => void;
  onOverlayClick: () => void;
  canManageAi: boolean;
  canManageUsers: boolean;
  canAuditMessenger: boolean;
  canInspectAccess: boolean;
  conversations: ChatHistoryItem[];
  chatFolders: ChatFolderHistoryItem[];
  userName: string;
  userEmail?: string;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const items = appNavItems({ canAuditMessenger });
  const chatsOpen = isChatNavActive(pathname);
  const showRailTip = !expanded;

  return (
    <SideNav
      className="app-side-nav"
      aria-label="Основная навигация"
      expanded={expanded}
      isRail
      addMouseListeners={false}
      addFocusListeners={false}
      onOverlayClick={onOverlayClick}
      onClick={(event) => {
        if (
          shouldToggleSideNavOnBackgroundClick(
            event.target as HTMLElement,
            overlay,
          )
        ) {
          onToggle();
        }
      }}
    >
      <SideNavItems>
        {items.map((item) => {
          if (item.id === "chat") {
            return (
              <SideNavMenu
                key={item.href}
                className="chat-nav-menu"
                title={item.label}
                data-rail-tip={showRailTip ? item.label : undefined}
                renderIcon={NAV_ICONS.chat}
                isActive={chatsOpen}
                defaultExpanded={expanded}
                onClickCapture={(event) => {
                  const target = event.target as HTMLElement;
                  if (!target.closest(".cds--side-nav__submenu")) {
                    return;
                  }
                  if (
                    target.closest(".cds--side-nav__submenu-chevron") ||
                    target.closest(".chat-nav-title-action") ||
                    target.closest(".chat-nav-folder-menu") ||
                    target.closest(".chat-nav-move") ||
                    target.closest(".chat-nav-delete")
                  ) {
                    return;
                  }
                  event.preventDefault();
                  event.stopPropagation();
                  router.push(newChatHref());
                }}
              >
                <ChatNavList items={conversations} folders={chatFolders} />
              </SideNavMenu>
            );
          }

          return (
            <AppSideNavLink
              key={item.href}
              href={item.href}
              label={item.label}
              renderIcon={NAV_ICONS[item.id]}
              isActive={isNavActive(pathname, item.href)}
              showRailTip={showRailTip}
            />
          );
        })}
      </SideNavItems>
      <div className="side-nav-end">
        <UserMenu
          name={userName}
          email={userEmail}
          expanded={expanded}
          showReports={canManageAi || canManageUsers}
          showAdmin={canShowAdminMenu({
            canManageUsers,
            canManageAi,
            canInspectAccess,
          })}
          adminHref={adminHomeHref({
            canManageUsers,
            canManageAi,
            canAuditMessenger,
            canInspectAccess,
          })}
        />
      </div>
    </SideNav>
  );
}
